import { useState } from "react";
import type { Sistema } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useSistemas } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { Tabela, type Coluna } from "@/componentes/Tabela";
import { Botao } from "@/componentes/Botao";
import { NovoSistema } from "./NovoSistema";
import { urlSemSegredo } from "./entrega";
import { Plus } from "lucide-react";
import { contagem, documento } from "@/formato";
import { rotaDaAplicacao } from "@/rotas";

/*
 * Aplicações — doc 06 §4.5 (o "sistema" do domínio; ver o rótulo abaixo).
 *
 * Contrato, chaves de acesso, entrega e consumo. A lista é curta por natureza
 * (são os clientes integrados), então não há busca nem paginação: uma barra de
 * busca sobre oito linhas é ruído. A linha é link real para a página da aplicação.
 */
export function Sistemas() {
  const { cliente, estado, pode } = useSessao();
  const [criando, setCriando] = useState(false);
  const ehAdmin = pode("admin");

  const { data, error, isPending } = useSistemas(cliente, estado.fase === "dentro");
  const sistemas = data?.sistemas ?? [];

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        {/*
          "Aplicações", e não "Sistemas", só no TEXTO da tela: rota da API,
          domínio e banco continuam `sistema` (doc 01 §3.1). Quem opera o painel
          pensa em "a aplicação do cliente"; "sistema" é vocabulário de quem
          escreveu o motor.
        */}
        <h1 className="text-titulo font-semibold">Aplicações</h1>
        <span className="rotulo">{isPending ? "carregando" : contagem(sistemas.length, "aplicação", "aplicações")}</span>

        {/*
          Esconder não é autorizar — quem recusa é a API, e a tentativa fica na
          auditoria. O botão some para quem não é `admin` porque oferecer uma
          ação que vai voltar `403` desperdiça o clique de quem opera.
        */}
        {ehAdmin && (
          <Botao className="ml-auto" onClick={() => setCriando(true)}>
            <Plus aria-hidden="true" className="size-4" />
            Nova aplicação
          </Botao>
        )}
      </header>

      <NovoSistema aberto={criando} aoFechar={() => setCriando(false)} />

      {error ? (
        <p role="alert" className="rounded-cartao border border-falha/40 bg-falha/10 px-4 py-3 text-falha">
          {textoDoErro(error)}
        </p>
      ) : (
        <Tabela
          colunas={COLUNAS}
          itens={sistemas}
          carregando={isPending}
          chaveDe={(sistema) => sistema.id}
          hrefDe={(sistema) => rotaDaAplicacao(sistema.id)}
          rotuloDe={(sistema) => sistema.nome}
          vazio="Nenhuma aplicação."
        />
      )}
    </section>
  );
}

const COLUNAS: readonly Coluna<Sistema>[] = [
  {
    chave: "nome",
    rotulo: "Aplicação",
    largura: "minmax(160px, 1.4fr)",
    render: (sistema) => <span className="truncate text-texto">{sistema.nome}</span>,
  },
  {
    chave: "documento",
    rotulo: "Documento",
    largura: "160px",
    // Em 1024 sai: quem varre esta lista procura contrato, entrega e status.
    descarte: 1,
    render: (sistema) => (
      <span className="dados truncate text-texto-2">{documento(sistema.documento)}</span>
    ),
  },
  {
    chave: "plano",
    rotulo: "Plano",
    largura: "minmax(110px, 1fr)",
    render: (sistema) => <span className="truncate text-texto-2">{sistema.plano?.nome || "—"}</span>,
  },
  {
    chave: "contrato",
    rotulo: "Contrato",
    largura: "120px",
    render: (sistema) =>
      sistema.plano?.vigente === true ? (
        <span className="text-pago">vigente</span>
      ) : (
        // Sem contrato vigente o sistema não emite — e isso precisa saltar na
        // varredura da lista, não só no detalhe.
        <span className="text-falha">sem vigência</span>
      ),
  },
  {
    chave: "entrega",
    rotulo: "Entrega",
    largura: "132px",
    render: (sistema) => {
      // É aqui que o alerta da visão geral cai: sem isto, a lista dizia "normal" e o operador
      // abria aplicação por aplicação para achar a que está descartando eventos.
      if (urlSemSegredo(sistema)) return <span className="text-transito">sem segredo</span>;

      const falhas = sistema.entrega?.falhas_consecutivas ?? 0;
      return (
        <span className={falhas > 0 ? "text-transito" : "text-texto-2"}>
          {sistema.entrega?.estado || "—"}
          {falhas > 0 ? ` · ${contagem(falhas, "falha", "falhas")}` : ""}
        </span>
      );
    },
  },
  {
    chave: "status",
    rotulo: "Status",
    largura: "96px",
    direita: true,
    render: (sistema) => <span className="text-texto-2">{sistema.status}</span>,
  },
];
